import useReservationsAdd from '../../../operations/client/useReservationAdd';
import { Box, Button, Checkbox, Dialog, Field, Flex, Grid, Input, Portal, Stack, Text, Textarea } from '@chakra-ui/react';
import { Client } from '@opanuj/shared/types';
import { ClientPicker } from '../../../components/ClientPicker';
import { daysOrder, formatSlotSummary } from '@opanuj/shared/utils';
import { useClientsList } from '../../../hooks/useClients';
import { useEffect, useState } from 'react';

export type SlotReservationFormProps = {
	slots: any[];
	availableSlots?: any[];
	initialClient?: Client;
	onClose: () => void;
};

type ReservationDraftStudent = {
	id: string;
	uid: string;
	name: string;
	grade: string;
	school: string;
};

type ReservationDraftLesson = {
	slotId: string;
	slotCode: string;
	notes: string;
	student: ReservationDraftStudent;
};

const getDaySortValue = (day?: string): number => {
	if (!day) return Number.MAX_SAFE_INTEGER;

	const normalized = String(day).trim().toLowerCase();
	const aliasMap: Record<string, string> = {
		poniedzialek: 'pon',
		pon: 'pon',
		wtorek: 'wt',
		wt: 'wt',
		sroda: 'śr',
		'środa': 'śr',
		sr: 'śr',
		czwartek: 'czw',
		czw: 'czw',
		piatek: 'pt',
		'piątek': 'pt',
		pt: 'pt',
		sobota: 'sob',
		sob: 'sob',
		niedziela: 'nd',
		nd: 'nd',
	};

	const originalDay = aliasMap[normalized] ?? normalized;
	const index = daysOrder.findIndex((weekday) => weekday.toLowerCase() === originalDay);
	return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
};

const sortSlotsByReservationOrder = (slots: any[] = []) => [...slots].sort((left, right) => {
	const dayDifference = getDaySortValue(left?.day ?? left?.weekday) - getDaySortValue(right?.day ?? right?.weekday);
	if (dayDifference !== 0) return dayDifference;

	const timeA = String(left?.time ?? '').trim();
	const timeB = String(right?.time ?? '').trim();
	if (timeA !== timeB) return timeA.localeCompare(timeB);

	const teacherA = String(left?.defaultTeacher ?? left?.teacher ?? '').trim();
	const teacherB = String(right?.defaultTeacher ?? right?.teacher ?? '').trim();
	return teacherA.localeCompare(teacherB, undefined, { sensitivity: 'base' });
});

const buildInitialLessonDrafts = (slots: any[]): ReservationDraftLesson[] => slots.map((slot) => ({
	slotId: slot.id,
	slotCode: slot.code ?? '',
	notes: '',
	student: {
		id: '',
		uid: '',
		name: '',
		grade: '',
		school: '',
	},
}));

export const SlotReservationForm = ({ slots, availableSlots = [], initialClient, onClose }: SlotReservationFormProps) => {
	const [clients] = useClientsList();
	const [selectedClient, setSelectedClient] = useState<Client | undefined>(initialClient);
	const [reservationNotes, setReservationNotes] = useState('');
	const [sendClientEmail, setSendClientEmail] = useState(false);
	const [lessonDrafts, setLessonDrafts] = useState<ReservationDraftLesson[]>(() => buildInitialLessonDrafts(slots));
	const [slotToAdd, setSlotToAdd] = useState('');
	const reservationMutation = useReservationsAdd({
		onSuccess: () => {
			onClose();
		},
	});

	useEffect(() => {
		setLessonDrafts(buildInitialLessonDrafts(slots));
	}, [slots]);

	useEffect(() => {
		setSelectedClient(initialClient ?? undefined);
	}, [initialClient]);

	const addableSlots = sortSlotsByReservationOrder((availableSlots ?? []).filter((slot) => !lessonDrafts.some((draft) => draft.slotId === slot.id)));
	const canSubmit = Boolean(selectedClient?.id) && lessonDrafts.length > 0 && lessonDrafts.every((lesson) => lesson.student.name.trim().length > 0);

	const updateLesson = (slotId: string, patch: Partial<ReservationDraftLesson>) => {
		setLessonDrafts((previous) => previous.map((lesson) => (
			lesson.slotId === slotId ? { ...lesson, ...patch } : lesson
		)));
	};

	const updateStudent = (slotId: string, field: keyof ReservationDraftStudent, value: string) => {
		setLessonDrafts((previous) => previous.map((lesson) => {
			if (lesson.slotId !== slotId) return lesson;
			return {
				...lesson,
				student: {
					...lesson.student,
					[field]: value,
				},
			};
		}));
	};

	const handleAddSlot = () => {
		if (!slotToAdd) return;
		const nextSlot = (availableSlots ?? []).find((slot) => slot.id === slotToAdd);
		if (!nextSlot) return;
		setLessonDrafts((previous) => [
			...previous,
			{
				slotId: nextSlot.id,
				slotCode: nextSlot.code ?? '',
				notes: '',
				student: {
					id: '',
					uid: '',
					name: '',
					grade: '',
					school: '',
				},
			},
		]);
		setSlotToAdd('');
	};

	const handleRemoveSlot = (slotId: string) => {
		setLessonDrafts((previous) => previous.filter((lesson) => lesson.slotId !== slotId));
	};

	const handleSubmit = () => {
		if (!selectedClient?.id || !canSubmit) return;

		reservationMutation.mutate({
			clientId: selectedClient.id,
			uid: selectedClient.uid,
			lessons: lessonDrafts.map((lesson) => ({
				slot: lesson.slotId,
				notes: lesson.notes.trim() || '',
				student: {
					id: selectedClient.id,
					uid: selectedClient.uid,
					name: lesson.student.name.trim(),
					grade: lesson.student.grade.trim(),
					level: '',
					school: lesson.student.school.trim(),
				},
			})),
			notes: reservationNotes.trim() || '',
			sendClientEmail,
			sendAdminEmail: false,
		});
	};

	return (
		<Stack gap={4}>
			<Box>
				<Field.Root>
					<Field.Label>
						Klient
						<Field.RequiredIndicator />
					</Field.Label>
					{initialClient ? (
						<Text fontWeight="semibold">{initialClient.name}</Text>
					) : (
						<ClientPicker
							clients={clients}
							selectedClient={selectedClient}
							onSelect={setSelectedClient}
						/>
					)}
				</Field.Root>
			</Box>

			<Stack gap={3}>
				{lessonDrafts.length === 0 && (
					<Box borderWidth="1px" borderRadius="md" p={4}>
						<Text color="gray.600">Brak wybranych terminów. Dodaj co najmniej jeden termin, aby zapisać rezerwację.</Text>
					</Box>
				)}
				{lessonDrafts.map((lesson) => {
					const lessonSlot = [...(availableSlots ?? []), ...slots].find((slot) => slot.id === lesson.slotId);
					const slotLabel = lessonSlot ? formatSlotSummary(lessonSlot) : (lesson.slotCode || `slot ${lesson.slotId || 'nowy'}`);
					return (
						<Box key={lesson.slotId || `${lesson.slotCode}-${lesson.student.name || 'new'}`} borderWidth="1px" borderRadius="md" p={3}>
							<Flex justify="space-between" align="center" gap={2} mb={2}>
								<Text fontWeight="semibold">{slotLabel}</Text>
								<Button
									variant="ghost"
									size="sm"
									colorPalette="red"
									onClick={() => handleRemoveSlot(lesson.slotId)}
								>
									Usuń termin
								</Button>
							</Flex>
							<Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap={3}>
								<Field.Root>
									<Field.Label>
										Imię ucznia
										<Field.RequiredIndicator />
									</Field.Label>
									<Input
										value={lesson.student.name}
										onChange={(event) => updateStudent(lesson.slotId, 'name', event.target.value)}
									/>
								</Field.Root>
								<Field.Root>
									<Field.Label>Szkoła</Field.Label>
									<Input
										value={lesson.student.school}
										onChange={(event) => updateStudent(lesson.slotId, 'school', event.target.value)}
									/>
								</Field.Root>
								<Field.Root>
									<Field.Label>Klasa</Field.Label>
									<Input
										value={lesson.student.grade}
										onChange={(event) => updateStudent(lesson.slotId, 'grade', event.target.value)}
									/>
								</Field.Root>
							</Grid>
							<Field.Root mt={3}>
								<Field.Label>Uwagi do tego terminu</Field.Label>
								<Textarea
									value={lesson.notes}
									onChange={(event) => updateLesson(lesson.slotId, { notes: event.target.value })}
									rows={2}
								/>
							</Field.Root>
						</Box>
					)
				})}
				{addableSlots.length > 0 && (
					<Flex gap={2} align="center">
						<select
							value={slotToAdd}
							onChange={(event) => setSlotToAdd(event.target.value)}
							style={{
								padding: '0.5rem 0.75rem',
								borderRadius: '0.5rem',
								border: '1px solid #E2E8F0',
								flex: 1,
							}}
						>
							<option value="">Dodaj kolejny termin</option>
							{addableSlots.map((slot) => (
								<option key={slot.id} value={slot.id}>{formatSlotSummary(slot)}</option>
							))}
						</select>
						<Button variant="outline" onClick={handleAddSlot} disabled={!slotToAdd}>
							Dodaj termin
						</Button>
					</Flex>
				)}
			</Stack>

			<Field.Root>
				<Field.Label>Uwagi do rezerwacji</Field.Label>
				<Textarea
					value={reservationNotes}
					onChange={(event) => setReservationNotes(event.target.value)}
					rows={3}
				/>
			</Field.Root>

			<Flex justify="space-between" align="center" gap={4} mt={2}>
				<Checkbox.Root checked={sendClientEmail} onCheckedChange={(details) => setSendClientEmail(Boolean(details.checked))}>
					<Checkbox.HiddenInput />
					<Checkbox.Control />
					<Checkbox.Label>Wyślij mail z potwierdzeniem do klienta</Checkbox.Label>
				</Checkbox.Root>
				<Flex gap={2}>
					<Button variant="outline" onClick={onClose}>Anuluj</Button>
					<Button colorPalette="orange" onClick={handleSubmit} disabled={!canSubmit || reservationMutation.isPending}>
						{reservationMutation.isPending ? 'Wysyłanie...' : 'Zapisz rezerwację'}
					</Button>
				</Flex>
			</Flex>
		</Stack>
	);
};

export type SlotReservationDialogProps = {
	open: boolean;
	slots: any[];
	availableSlots?: any[];
	initialClient?: Client;
	onClose: () => void;
};

export const SlotReservationDialog = ({ open, slots, availableSlots = [], initialClient, onClose }: SlotReservationDialogProps) => (
	<Dialog.Root
		open={open}
		onOpenChange={(event) => {
			if (!event.open) onClose();
		}}
	>
		<Portal>
			<Dialog.Backdrop />
			<Dialog.Positioner>
				<Dialog.Content maxW="960px" w="95vw">
					<Dialog.Header>
						<Dialog.Title>Rezerwuj termin(y)</Dialog.Title>
					</Dialog.Header>
					<Dialog.Body>
						<SlotReservationForm
							key={initialClient?.id ?? 'new-client'}
							availableSlots={availableSlots}
							initialClient={initialClient}
							slots={slots}
							onClose={onClose}
						/>
					</Dialog.Body>
				</Dialog.Content>
			</Dialog.Positioner>
		</Portal>
	</Dialog.Root>
);

export default SlotReservationForm;
